import { Component, Host, State, h } from "@stencil/core";
import {
    type Discount,
    DiscountType,
    type Offer,
    OfferType,
    type UpdatedCart,
} from "./interfaces";
import type { Product } from "@salla.sa/twilight/types/common";

@Component({
    tag: "salla-conditional-offer",
    styleUrl: "salla-conditional-offer.scss",
})
export class SallaConditionalOffer {

    @State() offer: Offer = null;
    @State() products: Record<string, Pick<Product, 'image' | 'url'>> = {}
    @State() isLoading = false
    @State() canRender = true

    componentWillLoad() {
        salla.onReady().then(() => {
            if (!salla.config.get('store.features')?.includes('conditional-offer') || (salla.storage.get("cart") !== '' && !salla.storage.get("cart")?.summary?.count)) {
                throw new Error('feature or cart object does not existed');
            }
        })
            .then(() => salla.api.cart.offers())
            .then(({ data }: { data: Offer[] }) => {
                this.offer = data.find(offer => offer.type === "conditional")

                if (!this.offer) {
                    this.canRender = false
                    return;
                }

                this.isLoading = true
                this.offer.details.discounts.unshift(({ value: 0, min_spend: 0 }))
                return this.updateInitialOfferValue()
            }).then(() => {
                if (!this.offer) return;
                return this.getProducts()
            }).then(() => {
                if (!this.offer) return;
                salla.event.on("cart::updated", (updatedCart: UpdatedCart) => this.updateOfferValues(updatedCart))
            })
            .catch(error => {
                this.canRender = false
                salla.logger.warn('salla-conditional-offer:: ', error)
            }).finally(() => {
                this.isLoading = false
            })

    }

    private getProducts() {
        const freeProductIDs = this.offer.details.discounts.filter(({ type }) => type === DiscountType.FREE_PRODUCT).map(({ value }) => value)
        if (freeProductIDs.length > 0) {
            return salla.product.fetch({ source: 'selected', source_value: freeProductIDs }).then(({ data }: { data: Array<Product> }) => {
                data.forEach(({ id, url, image }) => {
                    this.products[id] = { url, image }
                })
            })
        }
    }

    private updateInitialOfferValue() {
        return salla.api.cart.details().then(({ data: { cart } }: { data: { cart: UpdatedCart } }) => this.updateOfferValues(cart))
    }

    private updateOfferValues({ items, sub_total }: UpdatedCart) {
        const current_value = this.offer.details.based_on === OfferType.PRODUCT_COUNT ? items?.reduce((count: number, { quantity }) => count + quantity, 0) ?? 0 : sub_total
        //we need to assign the offer object a new reference to trigger re-rendering
        this.offer = {
            ...this.offer, details: {
                ...this.offer?.details, current_value,
            }
        }
        return this.offer
    }

    private getCheckpointContent(discount: Discount) {

        if (discount.type === DiscountType.PERCENTAGE) return `${discount.value}%`

        if (discount.type === DiscountType.FIXED) return salla.money(discount.value)

        if (discount.type !== DiscountType.FREE_PRODUCT) {
            salla.logger.error(`salla-conditional-offer:: unexpected type (${discount.type})!`);
            return "";
        }

        const productItem = this.products[discount.value]

        if (!productItem) {
            salla.logger.error(`salla-conditional-offer:: there is no product with id (${discount.value})!`);
            return "";
        }

        return <a class="s-conditional-offer-product-link" href={productItem.url} >
            <img
                class="s-conditional-offer-checkpoint-image-content"
                loading="lazy"
                decoding="async"
                alt={productItem?.image?.alt || ""}
                src={productItem?.image?.url} />
        </a>
    }

    private getOfferType(discount: Discount): string {
        const basedOn = this.offer.details?.based_on;
        if (basedOn === OfferType.PRODUCT_COUNT) return salla.lang.choice("blocks.header.products_count", discount.min_spend);
        if (basedOn === OfferType.PRODUCT_PURCHASE) return salla.money(discount.min_spend);

        salla.logger.warn(`salla-conditional-offer:: Unexpected offer detail's based_on value: ${basedOn}`);
        return `${discount.min_spend}`;
    }

    private clamp(value: number, min: number, max: number) {
        return Math.max(min, Math.min(value, max));
    }

    private mapValueRanges(value: number, initialMinRange: number, initialMaxRange: number, newMinRange: number, newMaxRange: number) {

        const newRange = ((value - initialMinRange) * (newMaxRange - newMinRange)) / (initialMaxRange - initialMinRange) + newMinRange;

        if (newRange === Number.POSITIVE_INFINITY) return 100

        if (newRange === Number.NEGATIVE_INFINITY) return 0

        return this.clamp(newRange, 0, 100)
    }

    private getCheckPointView(discount: Discount, index: number) {
        const checkpointIndex = this.offer.details.discounts.findIndex(({ min_spend }) => min_spend === discount.min_spend);
        const previousCheckpointValue = this.offer.details.discounts[checkpointIndex - 1]?.min_spend ?? 0;
        const progressPercentage = this.mapValueRanges(this.offer.details.current_value, previousCheckpointValue, discount.min_spend, 0, 100);
        const isActive = discount.min_spend <= this.offer.details.current_value;

        return (
            <div class="s-conditional-offer-checkpoint-container">
                {index > 0 ? [
                    <div key="progress-line" class="s-conditional-offer-progress-line-container">
                        <div class="s-conditional-offer-progress-line-inactive" />
                        <div class="s-conditional-offer-progress-line-active" style={{ width: `${progressPercentage}%` }} />
                    </div>,
                    <div key="checkpoint" class={`s-conditional-offer-checkpoint ${isActive ? "s-conditional-offer-active-checkpoint" : ""}`} >
                        <div class={`s-conditional-offer-item-avatar-content ${isActive ? "active" : ""}`} innerHTML={this.products && this.getCheckpointContent(discount)}>
                        </div>
                        <div class={`s-conditional-offer-checkpoint-label ${isActive ? "active" : ""}`} innerHTML={this.getOfferType(discount)} />

                    </div>
                ] :
                    <div key="label" class={{ "s-conditional-offer-checkpoint-label": true, "first-checkpoint": index === 0, active: isActive }} innerHTML={this.getOfferType(discount)} />
                }
            </div>
        );
    }

    private getLoadingSkeletonView() {
        return <Host class="s-conditional-offer-container" >
            <div class="s-conditional-offer-skeleton-inner-container">
                <div class="s-conditional-offer-skeleton-subtitle">
                    <salla-skeleton height="16px" width="30%" />
                </div>

                <div class="s-conditional-offer-skeleton-subtitle">
                    <salla-skeleton height="16px" width="35%" />
                </div>
                <div class="s-conditional-offer-skeleton-checkpoints-wrapper">
                    {Array(3).fill(null).map(() => (
                        [<salla-skeleton key="checkpoint-line" height="8px" />,
                        <div key="checkpoint">
                            <salla-skeleton height="60px" width="60px" type="circle" />
                        </div>]
                    ))}
                </div>

            </div>
        </Host>
    }

    render() {
        if (!this.canRender) return null
        if (this.isLoading) return this.getLoadingSkeletonView()
        if (!this.offer) return null

        return <Host class="s-conditional-offer-container">
            <div class="s-conditional-offer-title-wrapper">
                <div class="s-conditional-offer-title">{this.offer.title}</div>
                {this.offer.description ? <div class="s-conditional-offer-subtitle">
                    {this.offer.description} <i class="sicon-information" />
                </div> : null}
            </div>
            <div class="s-conditional-offer-progress-container">
                {this.offer.details.discounts.map((discount, index) => (
                    <div class={{ "flex-1": index > 0 }} key={discount.min_spend}>
                        {this.getCheckPointView(discount, index)}
                    </div>
                ))}
            </div>
        </Host>
    }
}
